import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Database, RefreshCw, Building2 } from "lucide-react";

export default function Diagnostics() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true); setErr("");
    api.get("/diagnostics", { timeout: 10000 })
      .then((r) => setD(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message || "Diagnostics unavailable"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="space-y-4" data-testid="diagnostics-loading"><div className="h-8 w-48 rounded-lg bg-muted animate-pulse" /><div className="grid grid-cols-3 gap-4">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-2xl bg-muted animate-pulse" />)}</div></div>;
  if (err) return (
    <Card className="p-8 text-center space-y-3 max-w-md mx-auto" data-testid="diagnostics-error">
      <p className="text-sm text-danger-foreground">{err}</p>
      <Button data-testid="diagnostics-retry" onClick={load} variant="outline"><RefreshCw size={14} className="mr-2" /> Retry</Button>
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="diagnostics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Activity size={22} className="text-primary" /> System Diagnostics</h1>
          <p className="text-sm text-muted-foreground">Platform health — visible to super admin only</p>
        </div>
        <Button variant="outline" data-testid="diagnostics-refresh" onClick={load}><RefreshCw size={14} className="mr-2" /> Refresh</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-5 rounded-2xl" data-testid="diag-db">
          <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Database size={12} /> Database</div>
          <div className={`font-mono text-xl font-bold mt-2 ${d.db === "up" ? "text-success" : "text-danger"}`}>{d.db}</div>
        </Card>
        <Card className="p-5 rounded-2xl" data-testid="diag-platform">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Platform</div>
          <div className="font-mono text-xl font-bold mt-2">{d.platform}</div>
        </Card>
        <Card className="p-5 rounded-2xl" data-testid="diag-tenants">
          <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Building2 size={12} /> Tenants</div>
          <div className="font-mono text-xl font-bold mt-2">{d.tenant_count}</div>
        </Card>
        <Card className="p-5 rounded-2xl" data-testid="diag-time">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Server time (UTC)</div>
          <div className="font-mono text-sm font-bold mt-2">{d.time?.slice(0, 19).replace("T", " ")}</div>
        </Card>
      </div>

      <Card className="p-5 rounded-2xl" data-testid="diag-collections">
        <h3 className="font-heading font-semibold mb-3 text-sm">Record counts per collection</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {Object.entries(d.collections).map(([name, count]) => (
            <div key={name} className="flex justify-between items-center text-sm border rounded-lg px-3 py-2" data-testid={`diag-coll-${name}`}>
              <span className="font-mono text-xs">{name}</span>
              <span className="font-mono font-bold">{count}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 rounded-2xl" data-testid="diag-tenant-list">
        <h3 className="font-heading font-semibold mb-3 text-sm">Tenants</h3>
        <div className="space-y-1.5">
          {d.tenants.map((t) => (
            <div key={t.slug} className="flex items-center gap-3 text-sm border rounded-lg px-3 py-2">
              <span className={`w-2 h-2 rounded-full ${t.status === "active" ? "bg-success" : "bg-danger"}`} />
              <span className="font-medium">{t.name}</span>
              <span className="text-xs text-muted-foreground font-mono">{t.slug}</span>
              <span className="text-xs text-muted-foreground">{t.industry_type} · {t.plan}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
